import * as htmlToImage from 'html-to-image';

const loadImage = (url, timeoutMs) =>
    new Promise((resolve) => {
        if (!url) {
            resolve();
            return;
        }

        const image = new Image();
        let settled = false;

        const finalize = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        image.onload = finalize;
        image.onerror = finalize;

        if (timeoutMs) {
            window.setTimeout(finalize, timeoutMs);
        }

        try {
            image.src = url;
        } catch (e) {
            finalize();
        }
    });

const getSvgImageHref = (node) =>
    node.getAttribute("href") ||
    node.getAttribute("xlink:href") ||
    node.getAttributeNS("http://www.w3.org/1999/xlink", "href");

const waitForSvgImages = async (svgEl, { timeoutMs = 2500 } = {}) => {
    if (!svgEl?.querySelectorAll) {
        return;
    }

    const urls = Array.from(svgEl.querySelectorAll("image"))
        .map(getSvgImageHref)
        .filter(Boolean)
        .map((href) => {
            if (href.startsWith("data:")) return href;
            try {
                return new URL(href, window.location.origin).toString();
            } catch (e) {
                return null;
            }
        })
        .filter(Boolean);

    const uniqueUrls = Array.from(new Set(urls));
    if (!uniqueUrls.length) {
        return;
    }

    await Promise.allSettled(uniqueUrls.map((url) => loadImage(url, timeoutMs)));
};

const getCaptureViewBox = (svgEl) => {
    if (!svgEl?.getBBox) return null;
    try {
        const bbox = svgEl.getBBox();
        if (!bbox?.width || !bbox?.height) return null;

        const maxDim = Math.max(bbox.width, bbox.height);
        const padding = Math.min(160, Math.max(40, maxDim * 0.04));

        const x = bbox.x - padding;
        const y = bbox.y - padding;
        const width = bbox.width + padding * 2;
        const height = bbox.height + padding * 2;

        return { x, y, width, height };
    } catch (e) {
        return null;
    }
};

const createSvgCloneForCapture = (svgEl, fixedViewBox = null) => {
    const clone = svgEl.cloneNode(true);

    if (fixedViewBox) {
        // Use the provided fixed viewBox to ensure consistent framing
        clone.setAttribute("viewBox", fixedViewBox);
    } else {
        // Fallback to dynamic viewBox calculation
        const captureViewBox = getCaptureViewBox(svgEl);
        if (captureViewBox) {
            clone.setAttribute(
                "viewBox",
                `${captureViewBox.x} ${captureViewBox.y} ${captureViewBox.width} ${captureViewBox.height}`
            );
        }
    }

    const widthPx = svgEl.clientWidth || svgEl.getBoundingClientRect?.().width || 0;
    const heightPx = svgEl.clientHeight || svgEl.getBoundingClientRect?.().height || 0;
    if (widthPx && heightPx) {
        clone.style.width = `${widthPx}px`;
        clone.style.height = `${heightPx}px`;
    }

    return clone;
};

export const captureConfiguredDoor = async (svgRef, fixedViewBox = null) => {
    if (!svgRef?.current) return null;
    try {
        const svgEl = svgRef.current;
        await waitForSvgImages(svgEl);

        // If no fixed viewBox is provided, try to get it from the original SVG
        const viewBoxToUse = fixedViewBox || svgEl.getAttribute("viewBox");
        
        const clone = createSvgCloneForCapture(svgEl, viewBoxToUse);

        const container = document.createElement("div");
        container.style.position = "fixed";
        container.style.left = "-10000px";
        container.style.top = "0";
        container.style.opacity = "0";
        container.style.pointerEvents = "none";
        container.style.width = `${svgEl.clientWidth || 0}px`;
        container.style.height = `${svgEl.clientHeight || 0}px`;

        container.appendChild(clone);
        document.body.appendChild(container);

        try {
            await waitForSvgImages(clone);
            const backgroundColor = window.getComputedStyle(svgEl).backgroundColor;
            const dataUrl = await htmlToImage.toPng(clone, { backgroundColor });
            return dataUrl;
        } finally {
            container.remove();
        }
    } catch (e) {
        console.error('Door image capture failed', e);
        return null;
    }
};
